/**
 * Gallet CMS Adapter
 *
 * Transforms Gallet-specific Contentful types to unified domain types.
 *
 * Field Mappings:
 * - All Gallet fields map 1:1 to domain types currently
 * - This adapter provides consistency layer for future divergence
 */

import { cleanCmsConfigValue } from '@/lib/contentful/preview';
import type * as Gallet from '@/graphql/contentful-gallet/api';
import { adaptFaqCategories, adaptFaqWrap } from './faq';
import { mergeSeoConfiguration } from '../seoConfiguration';
import { adaptPlaceholderService } from './placeholderService';
import type {
  CMSBanner,
  CMSBreadcrumb,
  CMSCallToAction,
  CMSCard,
  CMSCategoryBannerGrid,
  CMSCategoryBannerItem,
  CMSCityPair,
  CMSCollectionGroup,
  CMSCollectionItem,
  CMSContactUs,
  CMSContentGroup,
  CMSContentItem,
  CMSDoubleBanner,
  CMSEBoutiqueServices,
  CMSEvent,
  CMSFaq,
  CMSFaqCategories,
  CMSFindAStore,
  CMSFindAStoreData,
  CMSFreeTextContent,
  CMSGrid,
  CMSHeading,
  CMSHeroProductListing,
  CMSHeroSlide,
  CMSHeroSlider,
  CMSMedia,
  CMSNavQuickLinks,
  CMSPage,
  CMSPageHeader,
  CMSPlaceholderBoutiquesAbout,
  CMSPlaceholderBoutiquesInfo,
  CMSPlaceholder,
  CMSPlaceholderModelStory,
  CMSPlaceholderProductDetails,
  CMSPlaceholderService,
  CMSPlpTileOverride,
  CMSPopup,
  CMSProductCard,
  CMSResponsiveMedia,
  CMSSection,
  CMSSeoConfiguration,
  CMSServicePerk,
  CMSStoriesBanner,
  CMSStoriesList,
  CMSStorySlide,
  CMSSys,
  CMSTag,
  CMSTimeline,
  CMSTimelineEntry,
  CMSTrendingProducts,
  CMSUniversalCard,
  CMSUniversalGrid,
  UrlEventType,
  UrlType,
} from '../types';
import { POPUP_ANIMATION_MAP, POPUP_MAX_WIDTH_MAP } from '../types';

// oxlint-disable-next-line typescript/no-explicit-any
type GalletContentItem = any; // Simplified: Gallet queries return complex union types, using any for flexibility

// ============================================
// Base Adapters
// ============================================

/**
 * Adapt Contentful Sys to domain Sys
 */
function adaptSys(sys: Gallet.ContentfulSys | undefined): CMSSys {
  return {
    id: sys?.id || '',
    publishedAt: sys?.publishedAt || null,
  };
}

/**
 * Adapt Contentful Asset to domain Media
 */
function adaptMedia(asset: Gallet.ContentfulAsset | null | undefined): CMSMedia | null {
  if (!asset) return null;

  // URL can be direct (asset.url) or nested in image (asset.image.url)
  const imageAsset = (asset as { image?: { url?: string; title?: string } }).image;
  const url = asset.url || imageAsset?.url || '';

  return {
    url,
    title: asset.title || imageAsset?.title || null,
    alt: asset.title || (asset as { alt?: string }).alt || null,
    description: asset.description || null,
    width: asset.width || null,
    height: asset.height || null,
    contentType: asset.contentType || null,
    focalPoint: null,
  };
}

/**
 * Adapt an ImageWithFocalPoint wrapper to CMSMedia, propagating the focal point.
 * The wrapper holds `image` (the asset) and `focalPoint` (JSON: {x,y} or [x,y]).
 */
function adaptImageWithFocalPoint(
  wrapper: Record<string, unknown> | null | undefined,
  asset: Gallet.ContentfulAsset | null | undefined,
  // The wrapper's focal point is set against the DESKTOP image; do not attach
  // it to other assets (e.g. imageMobile) whose pixel space it doesn't match.
  withFocalPoint = true
): CMSMedia | null {
  const media = adaptMedia(asset);
  if (!media) return null;

  const focalPointData = (withFocalPoint ? wrapper?.focalPoint : null) as
    | { x?: number; y?: number; focalPoint?: { x?: number; y?: number } }
    | number[]
    | null
    | undefined;

  if (focalPointData) {
    if (Array.isArray(focalPointData) && focalPointData.length >= 2) {
      media.focalPoint = { x: focalPointData[0], y: focalPointData[1] };
    } else if (typeof focalPointData === 'object') {
      // Contentful sometimes wraps as { focalPoint: { x, y } }
      const point =
        'x' in focalPointData && 'y' in focalPointData
          ? focalPointData
          : (focalPointData as { focalPoint?: { x?: number; y?: number } }).focalPoint;
      if (point && typeof point.x === 'number' && typeof point.y === 'number') {
        media.focalPoint = { x: point.x, y: point.y };
      }
    }
  }

  // Carry an alt override from the ImageWithFocalPoint wrapper if present
  const altOverride = (wrapper?.alt as string) || null;
  if (altOverride) media.alt = altOverride;

  return media;
}

function adaptTags(
  tagsCollection: { items?: Array<{ key?: string; value?: string }> } | null | undefined
): CMSTag[] | null {
  const items = tagsCollection?.items;
  if (!items?.length) return null;
  return items
    .filter((item): item is { key: string; value: string } => Boolean(item?.key && item?.value))
    .map(({ key, value }) => ({ key, value }));
}

// ============================================
// Section Adapters
// ============================================

/**
 * Adapt PlaceholderProductDetails
 */
function adaptPlaceholderProductDetails(
  section: Extract<GalletContentItem, { __typename?: 'PlaceholderProductDetails' }>
): CMSPlaceholderProductDetails {
  const perks = section.perksCollection?.items?.map(adaptServicePerk).filter(Boolean) || [];

  return {
    __typename: 'PlaceholderProductDetails',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    perks: perks.length > 0 ? perks : null,
  };
}

/**
 * Adapt generic Placeholder
 */
function adaptPlaceholder(
  section: Extract<GalletContentItem, { __typename?: 'Placeholder' }>
): CMSPlaceholder | CMSPlaceholderBoutiquesInfo | CMSPlaceholderBoutiquesAbout {
  const tileItems = (section as { tileOverridesCollection?: { items?: unknown[] } })
    .tileOverridesCollection?.items;
  const initiallyCollapsed =
    (section as { initiallyCollapsed?: boolean | null }).initiallyCollapsed ?? null;

  const placeholderHeading = (section as { placeholderHeading?: string | null }).placeholderHeading;
  const type = cleanCmsConfigValue(section.type);
  const normalizedType = type?.trim().toLowerCase();
  const isBoutiqueInfo =
    normalizedType === 'boutique info detail' || normalizedType === 'boutique detail info';
  const isBoutiqueAbout =
    normalizedType === 'boutique detail about' || normalizedType === 'boutique story';

  const boutiqueTypename = isBoutiqueInfo
    ? 'PlaceholderBoutiquesInfo'
    : isBoutiqueAbout
      ? 'PlaceholderBoutiquesAbout'
      : 'Placeholder';

  return {
    __typename: boutiqueTypename,
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    type,
    heading: placeholderHeading ?? (section as { heading?: string | null }).heading ?? null,
    subtext: (section as { subtext?: { json: unknown } | null }).subtext ?? null,
    tileOverrides: adaptTileOverrides(tileItems),
    initiallyCollapsed,
  };
}

/**
 * Adapt the tileOverridesCollection to CMSPlpTileOverride[].
 * Drops entries that fail validation (out-of-range column, span overflow, no card).
 */
function adaptTileOverrides(items: unknown[] | undefined | null): CMSPlpTileOverride[] | null {
  if (!items?.length) return null;
  const out: CMSPlpTileOverride[] = [];
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as {
      __typename?: string;
      sys?: unknown;
      internalName?: string | null;
      row?: number | null;
      columnIndex?: number | null;
      span?: number | null;
      card?: GalletContentItem | null;
    };
    if (!item.card) continue;
    const row = Number(item.row);
    const columnIndex = Number(item.columnIndex);
    const span = Number(item.span);
    if (!Number.isFinite(row) || row < 1) continue;
    if (![1, 2, 3, 4].includes(columnIndex)) continue;
    if (![1, 3].includes(span)) continue;
    if (span === 3 && columnIndex > 2) continue; // 3-span must start at col 1 or 2
    out.push({
      __typename: 'PlpTileOverride',
      sys: adaptSys(item.sys as Parameters<typeof adaptSys>[0]),
      internalName: item.internalName || null,
      row,
      columnIndex,
      span,
      card: adaptCard(item.card),
    });
  }
  return out.length > 0 ? out : null;
}

/**
 * Adapt Contentful `PlaceholderModelStory` into the shared CMS domain type.
 * Passes through layout fields (media, background, mediaAlignment); PDP copy/image
 * are merged with Saleor in `renderPlaceholderModelStory`.
 */
function adaptPlaceholderModelStory(
  section: Extract<GalletContentItem, { __typename?: 'PlaceholderModelStory' }>
): CMSPlaceholderModelStory {
  return {
    __typename: 'PlaceholderModelStory',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    storyTitle: section.storyTitle?.text || null,
    storyContent: null, // TODO: Extract rich text content if needed
    media: adaptMedia(section.media),
    backgroundColor: cleanCmsConfigValue(section.backgroundColor),
    mediaAlignment: cleanCmsConfigValue(
      (section as { mediaAlignment?: string | null }).mediaAlignment
    ),
  };
}

/**
 * Adapt ServicePerk
 */
function adaptServicePerk(perk: unknown): CMSServicePerk | null {
  if (!perk || typeof perk !== 'object') return null;
  const p = perk as Record<string, unknown>;

  return {
    sys: p.sys ? adaptSys(p.sys as Gallet.ContentfulSys) : undefined,
    icon: p.icon ? adaptMedia(p.icon as Gallet.ContentfulAsset) : null,
    title: (p.heading as string) || (p.title as string) || null,
    description: (p.description as string) || null,
    type: cleanCmsConfigValue(p.type as string),
    modalText: p.modalText ? (p.modalText as { json: unknown }) : null,
    url: (p.url as string) || null,
    localizationConfiguration: adaptLocalizationConfiguration(p.localizationConfiguration),
  };
}

function adaptSliderHeading(rawHeading: unknown): CMSTrendingProducts['heading'] {
  const headingObj =
    rawHeading && typeof rawHeading === 'object' ? (rawHeading as Record<string, unknown>) : null;

  if (headingObj) {
    return {
      text: (headingObj.text as string) || null,
      headingType: cleanCmsConfigValue(headingObj.headingType as string),
      headingSeoTag: cleanCmsConfigValue(headingObj.headingSeoTag as string),
      headingVariation: cleanCmsConfigValue(headingObj.headingVariation as string),
      mainColor: extractColor(headingObj.mainColor),
      highlightColor: extractColor(headingObj.highlightColor),
      textAlignment: cleanCmsConfigValue(headingObj.textAlignment as string),
    };
  }

  if (typeof rawHeading === 'string') {
    return { text: rawHeading };
  }

  return null;
}

function adaptSliderBackgroundColor(rawBgColor: unknown): string | null {
  if (typeof rawBgColor === 'string') {
    return cleanCmsConfigValue(rawBgColor);
  }

  if (rawBgColor && typeof rawBgColor === 'object' && 'value' in rawBgColor) {
    return cleanCmsConfigValue((rawBgColor as { value?: string }).value);
  }

  return null;
}

/**
 * Read an optional CMS field without repeating the `'key' in section ? ... : null` guard.
 */
function optionalField<T>(section: Record<string, unknown>, key: string): T | null {
  return key in section ? (section[key] as T) : null;
}

/**
 * Adapt SliderProductsManual — SKU strings hydrated by the orchestrator.
 */
function adaptSliderProductsManual(
  section: Extract<GalletContentItem, { __typename?: 'SliderProductsManual' }>
): CMSTrendingProducts | null {
  const rawSkus = 'products' in section && Array.isArray(section.products) ? section.products : [];
  const productSkus = rawSkus.filter(
    (sku: unknown): sku is string => typeof sku === 'string' && sku.length > 0
  );

  if (productSkus.length === 0) return null;

  return {
    __typename: 'SliderProductsManual',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    heading: adaptSliderHeading(optionalField(section, 'heading')),
    products: [],
    productSkus,
    sliderType: cleanCmsConfigValue(optionalField<string>(section, 'type')),
    showCta: optionalField<boolean>(section, 'showCta') ?? null,
    showPrice: optionalField<boolean>(section, 'showPrice') ?? null,
    showName: optionalField<boolean>(section, 'showName') ?? null,
    showLabel: optionalField<boolean>(section, 'showLabel') ?? null,
    showWishlist: optionalField<boolean>(section, 'showWishlist') ?? null,
    slidesPerView: optionalField<number>(section, 'slidesPerView') ?? null,
    slidesPerViewMobile: optionalField<number>(section, 'slidesPerViewForMobile') ?? null,
    backgroundColor: adaptSliderBackgroundColor(optionalField(section, 'backgroundColor')),
    productCardBackgroundColor: cleanCmsConfigValue(
      optionalField<string>(section, 'productCardBackgroundColor')
    ),
    backgroundImage: adaptMedia(optionalField<Gallet.ContentfulAsset>(section, 'backgroundImage')),
    ctaText: optionalField<string>(section, 'ctaText') || null,
    ctaStyle: optionalField<string>(section, 'ctaStyle') || null,
    ctaUrl: optionalField<string>(section, 'ctaUrl') || null,
    ctaAriaLabel: optionalField<string>(section, 'ctaAriaLabel') || null,
    entryEffect: optionalField<boolean>(section, 'entryEffect') ?? null,
  };
}

/**
 * Adapt TrendingProducts — embedded product cards from CMS.
 */
function adaptTrendingProducts(
  section: Extract<GalletContentItem, { __typename?: 'TrendingProducts' }>
): CMSTrendingProducts | null {
  // Extract products from the section
  const rawProducts =
    ('productsCollection' in section ? section.productsCollection?.items : null) ||
    ('itemsCollection' in section ? section.itemsCollection?.items : null) ||
    [];

  const products = rawProducts
    .map(adaptProductCard)
    .filter((p: CMSProductCard | null): p is CMSProductCard => p !== null);

  if (products.length === 0) return null;

  return {
    __typename: section.__typename as 'TrendingProducts',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    heading: adaptSliderHeading('heading' in section ? section.heading : null),
    products,
    entryEffect: ('entryEffect' in section ? (section.entryEffect as boolean) : null) ?? null,
  };
}

/**
 * Adapt ProductCard
 */
function adaptProductCard(product: unknown): CMSProductCard | null {
  if (!product || typeof product !== 'object') return null;
  const p = product as Record<string, unknown>;

  return {
    sys: adaptSys(p.sys as Gallet.ContentfulSys),
    name: (p.name as string) || '',
    subtitle: (p.subtitle as string) || null,
    sku: (p.sku as string) || null,
    price: (p.price as number) || null,
    currency: (p.currency as string) || null,
    image: p.image ? adaptMedia(p.image as Gallet.ContentfulAsset) : null,
    href: (p.href as string) || null,
  };
}

/**
 * Adapt generic section (fallback)
 */
function adaptGenericSection(section: GalletContentItem): CMSSection {
  return {
    __typename: section.__typename || 'Unknown',
    sys: adaptSys(section.sys),
    internalName: ('internalName' in section ? section.internalName : null) as string | null,
  };
}

// ============================================
// Homepage/Page Section Adapters
// ============================================

/**
 * Adapt HeroSlider
 * Supports both Card and Page content types in slidesCollection.
 * Page items are mapped with sourceType='Page' for CTA label logic.
 */
function adaptHeroSlider(section: GalletContentItem): CMSHeroSlider {
  const slides = (section.slidesCollection?.items || [])
    .map((slide: unknown) => {
      if (!slide || typeof slide !== 'object') return null;
      const s = slide as Record<string, unknown>;
      const typename = s.__typename as string | undefined;

      // Page content type: map pageTitle, shortDescription, slug, thumbnail
      if (typename === 'Page') {
        return {
          __typename: 'HeroSlide' as const,
          sys: adaptSys(s.sys as Gallet.ContentfulSys),
          heading: (s.pageTitle as string) || null,
          subheading: (s.shortDescription as string) || null,
          media: s.thumbnail ? adaptResponsiveMedia(s.thumbnail) : null,
          video: null,
          cta: null,
          textColor: null,
          sourceType: 'Page' as const,
          slug: (s.slug as string) || null,
          localizationConfiguration: adaptLocalizationConfiguration(s.localizationConfiguration),
        };
      }

      // Card content type (default): existing mapping logic
      // heading/title can be a plain string or a Heading content-type reference { text }
      const headingField = s.heading ?? s.title;
      const slideHeading =
        typeof headingField === 'string'
          ? headingField
          : (headingField as { text?: string } | null)?.text || null;

      // CTA from referenced sub-entry (ctaRef), falling back to legacy flat fields
      const ctaFromRef = adaptCtaRef(s.ctaRef);
      let slideCta: CMSCallToAction | null = ctaFromRef;
      if (!slideCta && (s.url || s.urlText)) {
        const variant = (cleanCmsConfigValue(s.urlType as string) || 'buttonDefault') as UrlType;
        const ctaUrl = (s.url as string) || '';
        slideCta = {
          text: (s.urlText as string) || ctaUrl,
          url: ctaUrl,
          target: (s.openUrlInNewTab as boolean) ? '_blank' : '_self',
          variant,
          eventType: (s.url1EventType as UrlEventType) || undefined,
        };
      }

      const ctaRefObj = s.ctaRef as Record<string, unknown> | null | undefined;

      return {
        __typename: 'HeroSlide' as const,
        sys: adaptSys(s.sys as Gallet.ContentfulSys),
        heading: slideHeading,
        subheading: (s.subHeading as string) || (s.subtitle as string) || null,
        subtitleAbove: (s.subtitleAbove as string) || null,
        media: s.media ? adaptResponsiveMedia(s.media) : null,
        video: null,
        cta: slideCta,
        ctaVariant: cleanCmsConfigValue((ctaRefObj?.variant as string) || (s.urlType as string)),
        textColor: cleanCmsConfigValue(s.textColor as string) as 'light' | 'dark' | null,
        sourceType: 'Card' as const,
        slug: null,
        localizationConfiguration: adaptLocalizationConfiguration(s.localizationConfiguration),
      };
    })
    .filter(Boolean) as CMSHeroSlide[];

  return {
    __typename: 'HeroSlider',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    slides,
    // Support both Gallet naming (autoslide) and unified naming (autoplay)
    autoplay: section.autoslide ?? section.autoplay ?? false,
    interval: section.autoslideDurationMs ?? section.interval ?? 5000,
    dynamicBlogHero: section.dynamicBlogHero ?? false,
    fixToTop: section.fixToTop ?? false,
  };
}

const extractColor = (...fields: unknown[]): string | null => {
  const field = fields.find(Boolean);
  if (!field) return null;
  if (typeof field === 'string') return cleanCmsConfigValue(field);
  if (typeof field === 'object' && field !== null) {
    const obj = field as Record<string, unknown>;
    return cleanCmsConfigValue(
      (obj.value as string) || (obj.hex as string) || (obj.color as string)
    );
  }
  return null;
};

/**
 * Parse a grid column count from Contentful, stripping Content Source Maps
 * markers that preview mode injects into string fields.
 */
function parseGridColumnCount(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isInteger(raw) && raw >= 0) return raw;
  if (raw == null) return null;
  if (typeof raw !== 'string') return null;
  const cleaned = cleanCmsConfigValue(raw);
  if (!cleaned) return null;
  const match = /^\d+$/.exec(cleaned);
  return match ? Number(match[0]) : null;
}

/**
 * Extract plain text from Contentful rich text JSON
 */
function extractPlainTextFromRichText(
  richText: { json?: unknown } | null | undefined
): string | null {
  if (!richText?.json) return null;

  interface RichTextNode {
    nodeType?: string;
    value?: string;
    content?: RichTextNode[];
  }

  const extractText = (node: RichTextNode): string => {
    if (node.nodeType === 'text') {
      return node.value || '';
    }
    if (node.content && Array.isArray(node.content)) {
      return node.content.map(extractText).join('');
    }
    return '';
  };

  const doc = richText.json as RichTextNode;
  if (doc.content && Array.isArray(doc.content)) {
    return doc.content
      .filter((node) => node.nodeType === 'paragraph')
      .map((para) => extractText(para))
      .filter(Boolean)
      .join('\n\n');
  }

  return null;
}

/**
 * Adapt a CTA sub-entry reference to CMSCallToAction.
 * CTA fields now come from a referenced "Cta" content type.
 */
function adaptCtaRef(ctaRef: unknown): CMSCallToAction | null {
  if (!ctaRef || typeof ctaRef !== 'object') return null;
  const c = ctaRef as Record<string, unknown>;

  const url = (c.url as string) || '';
  if (!url && !c.text) return null;

  const variant = (cleanCmsConfigValue(c.variant as string) || 'buttonDefault') as UrlType;

  return {
    text: (c.text as string) || url,
    url,
    target: (c.openInNewTab as boolean) ? '_blank' : '_self',
    variant,
    eventType: (c.eventType as UrlEventType) || undefined,
    eventTrigger: (c.eventTrigger as CMSEvent) || null,
  };
}

function adaptRichText(raw: unknown): { json: unknown; links: undefined } | null {
  if (!raw || typeof raw !== 'object' || !('json' in raw)) return null;
  return { json: raw.json, links: undefined };
}

/**
 * Adapt Card (Gallet version)
 *
 * Maps Gallet Card fields to unified CMSCard type.
 * CTA, Typography, Layout, and Animation fields are unwrapped from referenced
 * sub-entries and flattened back to the existing CMSCard interface.
 */
function adaptCard(section: GalletContentItem): CMSCard {
  // Extract heading - Gallet uses nested title object with Heading fragment fields
  let heading: string | null = null;
  const titleField = section.title as
    | {
        text?: string;
        headingType?: string;
        headingSeoTag?: string;
        headingVariation?: string;
        mainColor?: string;
        highlightColor?: string;
        textAlignment?: string;
      }
    | string
    | null;
  if (typeof titleField === 'string') {
    heading = titleField;
  } else if (titleField && typeof titleField === 'object' && titleField.text) {
    heading = titleField.text;
  } else if (typeof section.heading === 'string') {
    heading = section.heading;
  }

  const titleObj = typeof titleField === 'object' && titleField ? titleField : null;
  const headingColor = extractColor(titleObj?.mainColor);
  const headingHighlightColor = extractColor(titleObj?.highlightColor);
  const headingTag = cleanCmsConfigValue(titleObj?.headingSeoTag);
  const headingType = cleanCmsConfigValue(titleObj?.headingType);
  const headingVariation = cleanCmsConfigValue(titleObj?.headingVariation);
  const headingTextAlignment = cleanCmsConfigValue(titleObj?.textAlignment);

  // Extract media - Gallet can have:
  // 1. ImageWithFocalPoint wrapper in 'media' field (e.g., ecom homepage)
  // 2. Direct image/video fields on the Card (e.g., teaser homepage)
  const media = section.media as {
    image?: Gallet.ContentfulAsset;
    imageMobile?: Gallet.ContentfulAsset;
    video?: Gallet.ContentfulAsset;
    videoMobile?: Gallet.ContentfulAsset;
  } | null;

  // Try media wrapper first, then fall back to direct fields.
  // ImageWithFocalPoint wrapper carries focal point info that must propagate.
  const image = media?.image
    ? adaptImageWithFocalPoint(media as Record<string, unknown>, media.image)
    : section.image
      ? adaptMedia(section.image as Gallet.ContentfulAsset)
      : null;
  const mobileImage = media?.imageMobile
    ? adaptImageWithFocalPoint(media as Record<string, unknown>, media.imageMobile, false)
    : section.mobileImage
      ? adaptMedia(section.mobileImage as Gallet.ContentfulAsset)
      : null;
  const video = media?.video
    ? adaptMedia(media.video)
    : section.video
      ? adaptMedia(section.video as Gallet.ContentfulAsset)
      : null;
  const mobileVideo = media?.videoMobile
    ? adaptMedia(media.videoMobile)
    : section.mobileVideo
      ? adaptMedia(section.mobileVideo as Gallet.ContentfulAsset)
      : null;

  // CTAs from referenced sub-entries (ctaRef/cta2Ref aliases in GraphQL)
  // Falls back to legacy flat fields and direct cta/primaryCta for backwards compatibility
  let cta = adaptCtaRef(section.ctaRef) || adaptCallToAction(section.cta) || null;
  if (!cta && section.primaryCta) {
    cta = adaptCallToAction(section.primaryCta);
  }
  // Legacy fallback: build CTA from flat url/urlText fields
  if (!cta && (section.url || section.urlText)) {
    const variant = ((section.urlType as string) || 'buttonDefault') as UrlType;
    const ctaUrl = (section.url as string) || '';
    cta = {
      text: (section.urlText as string) || ctaUrl,
      url: ctaUrl,
      target: (section.openUrlInNewTab as boolean) ? '_blank' : '_self',
      variant,
      eventType: (section.url1EventType as UrlEventType) || undefined,
      eventTrigger: (section.urlTrigger as CMSEvent) || null,
    };
  }

  let cta2 = adaptCtaRef(section.cta2Ref);
  // Legacy fallback: build CTA2 from flat url2 fields
  if (!cta2 && (section.url2 || section.url2Text)) {
    const variant2 = ((section.url2Type as string) || 'buttonOutlinePrimary') as UrlType;
    const cta2Url = (section.url2 as string) || '';

    if (cta2Url) {
      cta2 = {
        text: (section.url2Text as string) || cta2Url,
        url: cta2Url,
        target: section.openUrl2InNewTab === true ? '_blank' : '_self',
        variant: variant2,
        eventType: (section.url2EventType as UrlEventType) || undefined,
        eventTrigger: (section.url2Trigger as CMSEvent) || null,
      };
    }
  }

  // Unwrap referenced sub-entries
  const typography = section.typography as Record<string, unknown> | null;
  const layout = section.layout as Record<string, unknown> | null;
  const animation = section.animation as Record<string, unknown> | null;

  // Extract body text - check multiple sources
  let body: string | null = null;
  if (section.body) {
    body = section.body as string;
  } else if (section.description) {
    body = section.description as string;
  } else if (section.cardText) {
    const textField = section.cardText as { json?: unknown } | null;
    body = extractPlainTextFromRichText(textField);
  } else if (section.text) {
    const textField = section.text as { json?: unknown } | null;
    body = extractPlainTextFromRichText(textField);
  }

  // Background color: from layout sub-entry, or legacy flat fields
  const backgroundColor = extractColor(
    layout?.backgroundColor,
    section.backgroundColor,
    section.cardBackgroundColor
  );

  // CTA layout: split composite "direction-position" value back to separate fields
  const ctaLayout = (section.ctaLayout as string) || null;
  let urlDirection: string | null = null;
  let urlPosition: string | null = null;
  if (ctaLayout) {
    const dashIdx = ctaLayout.indexOf('-');
    if (dashIdx > 0) {
      urlDirection = ctaLayout.slice(0, dashIdx);
      urlPosition = ctaLayout.slice(dashIdx + 1);
    }
  }

  const tags: CMSTag[] | null = adaptTags(section.tagsCollection);

  return {
    __typename: 'Card',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    type: cleanCmsConfigValue(section.type) || null,
    heading,
    subHeading: section.subHeading || section.subtitle || null,
    subtitleAbove: (section.subtitleAbove as string) || null,
    body,
    richText: adaptRichText(section.cardText),
    richText2: adaptRichText(section.cardText2),
    image,
    mobileImage,
    video,
    mobileVideo,
    icon: section.icon ? adaptMedia(section.icon as Gallet.ContentfulAsset) : null,
    cta,
    cta2,
    ctaVariant: cleanCmsConfigValue(cta?.variant || (section.urlType as string)) || null,
    cta2Variant: cleanCmsConfigValue(cta2?.variant || (section.url2Type as string)) || null,
    urlDirection: cleanCmsConfigValue(urlDirection || (section.urlDirection as string)),
    urlPosition: cleanCmsConfigValue(urlPosition || (section.urlPosition as string)),

    // Flattened from typography sub-entry (with legacy fallbacks)
    // cleanCmsConfigValue strips Content Source Maps encoding that breaks
    // strict equality checks and CSS value parsing in preview mode (BEC-23347)
    textColor: extractColor(typography?.textColor, section.textColor),
    textAlignment: cleanCmsConfigValue(
      (typography?.textAlignment as string) || section.textAlignment
    ) as 'left' | 'center' | 'right' | null,
    textSize: cleanCmsConfigValue((typography?.textSize as string) || section.textSize) as
      | 'default'
      | 'compact'
      | 'large'
      | null,

    // Heading styling (from Heading sub-entry on title field)
    headingColor,
    headingHighlightColor,
    headingTag,
    headingType,
    headingVariation,
    headingTextAlignment: headingTextAlignment as 'left' | 'center' | 'right' | null,

    // Flattened from layout sub-entry (with legacy fallbacks)
    backgroundColor,
    contentAlignment: cleanCmsConfigValue(
      (layout?.contentAlignment as string) || section.contentAlignment
    ),
    contentMaxWidth: cleanCmsConfigValue(
      (layout?.contentMaxWidth as string) || section.contentMaxWidth
    ),
    contentOption: cleanCmsConfigValue(
      (layout?.contentOption as string) || (section.contentOption as string)
    ),
    verticalAlignment: cleanCmsConfigValue(
      (typography?.verticalAlignment as string) ||
        (layout?.verticalAlignment as string) ||
        section.verticalAlignment
    ) as 'center' | 'bottom' | 'top' | null,
    backgroundAlignment: cleanCmsConfigValue(
      (layout?.backgroundAlignment as string) || section.backgroundAlignment
    ),
    componentHeight:
      (layout?.componentHeight as number) || (section.componentHeight as number) || null,
    componentFullHeight: (layout?.componentFullHeight as boolean) ?? null,
    componentPadding: cleanCmsConfigValue(
      (layout?.componentPadding as string) || (section.componentPadding as string)
    ),
    marginTop: cleanCmsConfigValue((layout?.marginTop as string) || (section.marginTop as string)),
    marginBottom: cleanCmsConfigValue(
      (layout?.marginBottom as string) || (section.marginBottom as string)
    ),

    // Flattened from animation sub-entry (with legacy fallbacks)
    mediaAnimationEffect: cleanCmsConfigValue(
      (animation?.effect as string) || (section.mediaAnimationEffect as string)
    ),
    animationTriggerPoint: cleanCmsConfigValue(
      (animation?.triggerPoint as string) || (section.animationTriggerPoint as string)
    ),
    animationSpeed: cleanCmsConfigValue(
      (animation?.speed as string) || (section.animationSpeed as string)
    ),
    transitionType: cleanCmsConfigValue(
      (animation?.transitionType as string) || (section.transitionType as string)
    ),

    tags,
    entryEffect: (section.entryEffect as boolean) ?? null,

    visibleInDesktop:
      (layout?.visibleInDesktop as boolean) ?? (section.visibleInDesktop as boolean) ?? null,
    visibleInMobile:
      (layout?.visibleInMobile as boolean) ?? (section.visibleInMobile as boolean) ?? null,
  };
}

/**
 * Adapt ContentGroup
 *
 * Schema fields:
 * - type: The layout type (e.g., 'DoubleCard', 'ThreeColumnStack', 'ThreeColumnScroll')
 * - itemsCollection: Collection of child content items
 */
function adaptContentGroup(section: GalletContentItem): CMSContentGroup {
  const items = (section.itemsCollection?.items || [])
    .map((item: unknown) => adaptGalletSection(item as GalletContentItem))
    .filter(Boolean) as CMSContentItem[];

  // ContentGroup uses 'type' field for layout type (DoubleCard, ThreeColumnStack, etc.)
  const groupType = cleanCmsConfigValue(section.type as string);

  return {
    __typename: 'ContentGroup',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    heading: null, // ContentGroup schema doesn't have heading field
    type: groupType, // The actual type from Contentful (DoubleCard, etc.)
    layout: null, // layout is for legacy 'grid'|'stack'|'carousel' values
    items,
  };
}

/**
 * Adapt Banner
 */
function adaptBanner(section: GalletContentItem): CMSBanner {
  // GraphQL Banner fragment uses aliases:
  //   bannerHeading: heading, bannerText: text { json }, bannerBackgroundColor: backgroundColor
  // Image is an ImageWithFocalPoint (not media), CTA is url/urlText/urlType (not primaryCta)

  // Build CTA from individual url/urlText/urlType fields
  const cleanedUrlType = cleanCmsConfigValue(section.urlType as string);
  const hasCta = section.url && section.urlText && cleanedUrlType !== 'none';
  const cta: CMSCallToAction | null = hasCta
    ? {
        text: section.urlText,
        url: section.url,
        target: null,
        variant: (cleanedUrlType || 'buttonDefault') as UrlType,
        icon: null,
      }
    : null;

  // Image comes as ImageWithFocalPoint (has nested .image asset)
  const responsiveMedia = section.image ? adaptResponsiveMedia(section.image) : null;

  return {
    __typename: 'Banner',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    heading: section.bannerHeading || section.heading || section.title || null,
    body: section.body || section.description || null,
    bannerText: section.bannerText || null,
    media: responsiveMedia?.desktop || (section.media ? adaptMedia(section.media?.image) : null),
    image: responsiveMedia?.desktop || null,
    cta: cta || (section.primaryCta ? adaptCallToAction(section.primaryCta) : null),
    textColor: cleanCmsConfigValue(section.textColor) as 'light' | 'dark' | null,
    backgroundColor: extractColor(section.bannerBackgroundColor, section.backgroundColor),
    imagePosition: cleanCmsConfigValue(section.imagePosition) as 'left' | 'right' | null,
    textAlignment: cleanCmsConfigValue(section.textAlignment) as 'left' | 'center' | 'right' | null,
  };
}

/**
 * Adapt StoriesBanner (Gallet-specific)
 *
 * Schema fields:
 * - storySlidesCollection (not slidesCollection)
 * - cityPairsCollection (not citiesCollection)
 * - featuredStory, worldClockImage, contentPanelBackgroundMedia
 * - layout, label, bodyText, ctaText, ctaUrl
 * - contentPanelBackground, contentPanelBackgroundColor
 * - ringPosition, showOnlyTopCity, autoslideDuration
 */
function adaptStoriesBanner(section: GalletContentItem): CMSStoriesBanner {
  // Use correct field name: storySlidesCollection
  // Story slide schema: title, description, backgroundImage (direct Asset), ctaText, ctaUrl
  const stories = (section.storySlidesCollection?.items || [])
    .map((slide: unknown) => {
      if (!slide || typeof slide !== 'object') return null;
      const s = slide as Record<string, unknown>;

      // backgroundImage is a direct Asset with url, not ImageWithFocalPoint wrapper
      const bgImage = s.backgroundImage as { url?: string } | null;
      const media: CMSMedia | null = bgImage?.url
        ? {
            url: bgImage.url,
            alt: null,
            title: null,
            description: null,
            width: null,
            height: null,
            contentType: null,
            focalPoint: null,
          }
        : null;

      // CTA from ctaText/ctaUrl fields (not primaryCta)
      const ctaText = s.ctaText as string | null;
      const ctaUrl = s.ctaUrl as string | null;
      const cta: CMSCallToAction | null = ctaUrl
        ? {
            text: ctaText || 'Read more',
            url: ctaUrl,
            target: '_self',
            variant: 'outline-white' as const, // Gallet stories use outline white buttons
          }
        : null;

      return {
        sys: adaptSys(s.sys as Gallet.ContentfulSys),
        title: (s.title as string) || null,
        description: (s.description as string) || null,
        media,
        cta,
      };
    })
    .filter(Boolean) as CMSStorySlide[];

  // Use correct field name: cityPairsCollection
  // CityPair schema: city12 (e.g., "New York"), city24 (e.g., "Tokyo"), rotation
  const cities = (section.cityPairsCollection?.items || [])
    .map((city: unknown) => {
      if (!city || typeof city !== 'object') return null;
      const c = city as Record<string, unknown>;
      return {
        sys: c.sys ? adaptSys(c.sys as Gallet.ContentfulSys) : undefined,
        city12: (c.city12 as string) || null,
        city24: (c.city24 as string) || null,
        rotation: (c.rotation as number) ?? null,
      };
    })
    .filter(Boolean) as CMSCityPair[];

  // Extract featured story (same structure as storySlidesCollection items)
  let featuredStory: CMSStorySlide | null = null;
  if (section.featuredStory) {
    const fs = section.featuredStory as Record<string, unknown>;
    // backgroundImage is a direct Asset with url
    const fsBgImage = fs.backgroundImage as { url?: string } | null;
    const fsMedia: CMSMedia | null = fsBgImage?.url
      ? {
          url: fsBgImage.url,
          alt: null,
          title: null,
          description: null,
          width: null,
          height: null,
          contentType: null,
          focalPoint: null,
        }
      : null;
    // CTA from ctaText/ctaUrl
    const fsCtaText = fs.ctaText as string | null;
    const fsCtaUrl = fs.ctaUrl as string | null;
    const fsCta: CMSCallToAction | null = fsCtaUrl
      ? {
          text: fsCtaText || 'Read more',
          url: fsCtaUrl,
          target: '_self',
          variant: 'outline-white' as const,
        }
      : null;

    featuredStory = {
      sys: adaptSys(fs.sys as Gallet.ContentfulSys),
      title: (fs.title as string) || null,
      description: (fs.description as string) || null,
      media: fsMedia,
      cta: fsCta,
    };
  }

  // Extract world clock image
  const worldClockImage = section.worldClockImage
    ? adaptMedia(
        (section.worldClockImage as Record<string, unknown>).image as Gallet.ContentfulAsset
      )
    : null;

  // Extract content panel background media
  const contentPanelBackgroundMedia = section.contentPanelBackgroundMedia
    ? adaptMedia(
        (section.contentPanelBackgroundMedia as Record<string, unknown>)
          .image as Gallet.ContentfulAsset
      )
    : null;

  return {
    __typename: 'StoriesBanner',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    // Content fields (headingText is aliased from heading in GraphQL fragment)
    layout: (section.layout as string) || null,
    label: (section.label as string) || null,
    heading: (section.headingText as string) || (section.heading as string) || null,
    bodyText: (section.bodyText as string) || null,
    ctaText: (section.ctaText as string) || null,
    ctaUrl: (section.ctaUrl as string) || null,
    // Visual configuration
    contentPanelBackground: (section.contentPanelBackground as string) || null,
    contentPanelBackgroundColor: extractColor(section.contentPanelBackgroundColor),
    contentPanelBackgroundMedia,
    worldClockImage,
    ringPosition: (section.ringPosition as string) || null,
    showOnlyTopCity: (section.showOnlyTopCity as boolean) ?? null,
    autoslideDuration: (section.autoslideDuration as number) ?? null,
    // Collections
    featuredStory,
    stories: stories.length > 0 ? stories : null,
    cities: cities.length > 0 ? cities : null,
  };
}

/**
 * Adapt Timeline parent (Gallet-specific)
 *
 * Contains an entriesCollection of TimelineEntry children and a shared ctaLabel.
 * Schema fields (entry): year, title, backgroundImage (ImageWithFocalPoint —
 * a "media wrapper" carrying separate desktop/mobile assets)
 */
function adaptTimeline(section: GalletContentItem): CMSTimeline {
  const entries = (section.entriesCollection?.items || [])
    .map((entry: unknown) => {
      if (!entry || typeof entry !== 'object') return null;
      const e = entry as Record<string, unknown>;

      const cta =
        e.cta && typeof e.cta === 'object' ? adaptPopup(e.cta as GalletContentItem) : null;

      return {
        __typename: 'TimelineEntry' as const,
        sys: adaptSys(e.sys as Gallet.ContentfulSys),
        year: e.year as number,
        title: (e.title as string) || null,
        // backgroundImage is an ImageWithFocalPoint, so split it into a
        // desktop/mobile pair for MediaWrapper instead of collapsing to one asset.
        backgroundImage: adaptResponsiveMedia(e.backgroundImage),
        cta,
      };
    })
    .filter(Boolean) as CMSTimelineEntry[];

  return {
    __typename: 'Timeline',
    sys: adaptSys(section.sys),
    id: (section.id as string) || null,
    internalName: section.internalName || null,
    ctaLabel: (section.ctaLabel as string) || null,
    entries,
    stickyScroll: !!section.stickyScroll,
  };
}

/**
 * Adapt CategoryBannerGrid (Gallet-specific)
 *
 * Contentful uses `bannersCollection` field containing `CategoryBannerItem` entries.
 * Schema fields:
 * - Grid: gridColumns, gridGap, marginTop, marginBottom, componentPadding, bannerAspectRatio, transitionType
 * - Items: hoverEffect, transitionType, textAlignment, contentPosition, textColorPreset, etc.
 */
function adaptCategoryBannerGrid(section: GalletContentItem): CMSCategoryBannerGrid {
  // Field is bannersCollection, not itemsCollection
  const items = (section.bannersCollection?.items || [])
    .map((item: unknown) => {
      if (!item || typeof item !== 'object') return null;
      const i = item as Record<string, unknown>;

      // Extract background media (ImageWithFocalPoint wrapper)
      const bgMedia = i.backgroundMedia as {
        image?: Gallet.ContentfulAsset;
      } | null;
      const productImg = i.productImage as {
        image?: Gallet.ContentfulAsset;
      } | null;

      // Build CTA from href/ctaText
      let cta: CMSCallToAction | null = null;
      if (i.href) {
        cta = {
          text: (i.ctaText as string) || 'View Collection',
          url: i.href as string,
          target: '_self',
          variant: 'primary',
        };
      }

      return {
        sys: adaptSys(i.sys as Gallet.ContentfulSys),
        title: (i.title as string) || null,
        subtitle: (i.subtitle as string) || null,
        media: bgMedia?.image ? adaptMedia(bgMedia.image) : null,
        productImage: productImg?.image ? adaptMedia(productImg.image) : null,
        cta,
        backgroundColor: extractColor(i.backgroundColor),
        // Item-level configuration
        hoverEffect: cleanCmsConfigValue(i.hoverEffect as string),
        transitionType: cleanCmsConfigValue(i.transitionType as string),
        textAlignment: cleanCmsConfigValue(i.textAlignment as string),
        contentPosition: cleanCmsConfigValue(i.contentPosition as string),
        textColorPreset: extractColor(i.textColorPreset),
        textColorCustom: extractColor(i.textColorCustom),
        overlayOpacity: (i.overlayOpacity as number) ?? null,
        parallaxOverlayColorPreset: cleanCmsConfigValue(i.parallaxOverlayColorPreset as string),
        parallaxOverlayColorCustom: cleanCmsConfigValue(i.parallaxOverlayColorCustom as string),
        ctaColor: cleanCmsConfigValue(i.ctaColor as string),
      };
    })
    .filter(Boolean) as CMSCategoryBannerItem[];

  return {
    __typename: 'CategoryBannerGrid',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    heading: section.heading || null,
    items,
    // Grid-level configuration
    gridColumns: cleanCmsConfigValue(section.gridColumns as string),
    gridGap: cleanCmsConfigValue(section.gridGap as string),
    marginTop: cleanCmsConfigValue(section.marginTop as string),
    marginBottom: cleanCmsConfigValue(section.marginBottom as string),
    componentPadding: cleanCmsConfigValue(section.componentPadding as string),
    bannerAspectRatio: cleanCmsConfigValue(section.bannerAspectRatio as string),
    transitionType: cleanCmsConfigValue(section.transitionType as string),
    backgroundImage: section.backgroundImage ? adaptMedia(section.backgroundImage) : null,
  };
}

/**
 * Adapt DoubleBanner (Gallet-specific)
 */
function adaptDoubleBanner(section: GalletContentItem): CMSDoubleBanner {
  return {
    __typename: 'DoubleBanner',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    leftBanner: section.leftBanner ? adaptBanner(section.leftBanner) : null,
    rightBanner: section.rightBanner ? adaptBanner(section.rightBanner) : null,
  };
}

/**
 * Adapt EBoutiqueServices (Gallet-specific)
 */
function adaptEBoutiqueServices(section: GalletContentItem): CMSEBoutiqueServices {
  const services = (section.servicesCollection?.items || [])
    .map(adaptServicePerk)
    .filter(Boolean) as CMSServicePerk[];

  return {
    __typename: 'EBoutiqueServices',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    heading: section.heading || null,
    services,
  };
}

export function adaptPopup(section: GalletContentItem): CMSPopup {
  const content = (section.contentCollection?.items || [])
    .map((item: GalletContentItem) => adaptGalletSection(item))
    .filter(Boolean) as CMSContentItem[];

  const rawMaxWidth = (section.maxWidth as string) || null;
  const maxWidth = rawMaxWidth ? (POPUP_MAX_WIDTH_MAP[rawMaxWidth.toLowerCase()] ?? null) : null;

  const rawAnimation = (section.animation as string) || null;
  const animation = rawAnimation ? (POPUP_ANIMATION_MAP[rawAnimation.toLowerCase()] ?? null) : null;

  const backgroundColor = extractColor(section.backgroundColor);

  return {
    __typename: 'Popup',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    animation,
    maxWidth,
    backgroundColor,
    content: content.length > 0 ? content : null,
  };
}

/**
 * Adapt HeroProductListing (PLP Hero)
 */
function adaptHeroProductListing(section: GalletContentItem): CMSHeroProductListing {
  // Handle collection items if present
  const collectionItems: CMSCollectionItem[] = (section.collectionItemsCollection?.items || [])
    .map((item: unknown) => {
      if (!item || typeof item !== 'object') return null;
      const i = item as Record<string, unknown>;
      return {
        sys: adaptSys(i.sys as Gallet.ContentfulSys),
        name: (i.name as string) || null,
        slug: (i.slug as string) || null,
        image: i.image ? adaptMedia(i.image as Gallet.ContentfulAsset) : null,
        description: (i.description as string) || null,
      };
    })
    .filter(Boolean) as CMSCollectionItem[];

  const backgroundColor = extractColor(section.backgroundColor);

  return {
    __typename: 'HeroProductListing',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    type: cleanCmsConfigValue(section.type),
    // Note: Fragment uses aliases heroHeading, heroSubheader, heroSubheading
    heading: section.heroHeading || section.heading || null,
    headingNumberOfColoredWords: section.headingNumberOfColoredWords || null,
    headingSeoTag: cleanCmsConfigValue(section.headingSeoTag),
    headingStyle: cleanCmsConfigValue(section.headingStyle),
    subheader: section.heroSubheader || section.subheader || null,
    subheading: section.heroSubheading || section.subheading || null,
    subheaderIsCapitalized: section.subheaderIsCapitalized ?? null,
    textBelowMedia: section.textBelowMedia ?? null,
    textAlignment: cleanCmsConfigValue(section.textAlignment) as 'left' | 'center' | 'right' | null,
    textColor: extractColor(section.textColor),
    showFilters: section.showFilters ?? null,
    showBreadcrumbs: section.showBreadcrumbs ?? null,
    backgroundColor,
    backgroundColorOpacity: section.backgroundColorOpacity ?? null,
    media: section.media ? adaptMedia(section.media?.image || section.media) : null,
    collectionItems: collectionItems.length > 0 ? collectionItems : null,
  };
}

function adaptContactUs(section: GalletContentItem): CMSContactUs {
  const contactHeader = section.contactHeader as Record<string, unknown> | null | undefined;
  const titleFallback = (section.title as string) || (section.heading as string) || null;

  const heading = contactHeader
    ? {
        text: (contactHeader.text as string) || titleFallback,
        headingType: cleanCmsConfigValue(contactHeader.headingType as string),
        headingSeoTag: cleanCmsConfigValue(contactHeader.headingSeoTag as string),
        headingVariation: cleanCmsConfigValue(contactHeader.headingVariation as string),
        mainColor: extractColor(contactHeader.mainColor),
        highlightColor: extractColor(contactHeader.highlightColor),
        textAlignment: cleanCmsConfigValue(contactHeader.textAlignment as string),
      }
    : titleFallback
      ? { text: titleFallback }
      : null;

  const bgRef = section.backgroundMedia as { image?: Gallet.ContentfulAsset } | null | undefined;
  const backgroundMedia = bgRef?.image ? adaptMedia(bgRef.image) : null;

  return {
    __typename: 'ContactUs',
    sys: adaptSys(section.sys),
    internalName: (section.internalName as string) || null,
    type: cleanCmsConfigValue(section.type as string),
    heading,
    description: (section.subtitle as string) || (section.description as string) || null,
    phone: (section.phone as string) || null,
    email: (section.email as string) || null,
    cta: adaptCallToAction(section.cta) || null,
    backgroundMedia,
  };
}

// ============================================
// Helper Adapters
// ============================================

/**
 * Adapt responsive media (desktop + mobile)
 */
/**
 * Adapt CollectionGroup. Products are populated later by the page orchestrator
 * based on either an automatic collection query or an explicit SKU list.
 */
function adaptCollectionGroup(section: GalletContentItem): CMSCollectionGroup {
  const productReferences = section.productReferences as Record<string, unknown> | null;
  const referencesTypename = productReferences?.__typename as string | undefined;

  let collectionName: string | null = null;
  let algoliaFacetFilters: string | null = null;
  let maxProducts: number | null = null;
  let sortBy: string | null = null;
  let productSkus: string[] | null = null;

  if (referencesTypename === 'AutomaticCollectionGroup') {
    collectionName = (productReferences?.collection as string) || null;
    const filters = productReferences?.algoliaFacetFilters;
    algoliaFacetFilters = Array.isArray(filters) ? filters.join(',') : (filters as string) || null;
    maxProducts = (productReferences?.maximumNumberOfProducts as number) || null;
    sortBy = (productReferences?.sortBy as string) || null;
  } else if (referencesTypename === 'ManualCollectionGroupProductReferences') {
    productSkus = (productReferences?.manualProductReferences as string[]) || null;
  }

  const endImageData = section.endImage as Record<string, unknown> | null;
  let endCard: CMSCollectionGroup['endCard'] = null;
  if (endImageData) {
    const endImageInner = endImageData.endImage as Record<string, unknown> | null;
    const endImageAsset = endImageInner?.image as Gallet.ContentfulAsset | null | undefined;
    endCard = {
      sys: adaptSys(endImageData.sys as Gallet.ContentfulSys),
      image: adaptImageWithFocalPoint(endImageInner, endImageAsset),
      linkUrl: (endImageData.endImageUrl as string) || null,
      linkText: (endImageData.endImageUrlText as string) || null,
      linkType: (endImageData.endImageLinkType as string) || null,
    };
  }

  const bannerData = section.banner as Record<string, unknown> | null;
  let banner: CMSBanner | null = null;
  if (bannerData) {
    const bannerImage = bannerData.image as Record<string, unknown> | null;
    const bannerAsset = bannerImage?.image as Gallet.ContentfulAsset | null | undefined;
    banner = {
      __typename: 'Banner',
      sys: adaptSys(bannerData.sys as Gallet.ContentfulSys),
      internalName: (bannerData.internalName as string) || null,
      title: (bannerData.title as string) || null,
      heading: (bannerData.title as string) || null,
      text: (bannerData.text as string) || null,
      body: null,
      bannerText: null,
      image: adaptImageWithFocalPoint(bannerImage, bannerAsset),
      media: adaptImageWithFocalPoint(bannerImage, bannerAsset),
      cta:
        bannerData.url || bannerData.urlText
          ? {
              text: (bannerData.urlText as string) || 'Discover the collection',
              url: (bannerData.url as string) || '#',
              target: '_self',
              variant: ((bannerData.urlType as string) || 'buttonDefault') as UrlType,
            }
          : null,
      ctaType: (bannerData.urlType as string) || null,
      textColor: null,
      backgroundColor: null,
      imagePosition: null,
      textAlignment: null,
    };
  }

  const tileItems = (section as { tileOverridesCollection?: { items?: unknown[] } })
    .tileOverridesCollection?.items;

  return {
    __typename: 'CollectionGroup',
    sys: adaptSys(section.sys),
    internalName: (section.internalName as string) || null,
    heading: (section.internalName as string) || null,
    type: (section.type as string) || null,
    banner,
    collectionName,
    algoliaFacetFilters,
    maxProducts,
    sortBy,
    productSkus,
    products: [], // populated by orchestrator
    endCard,
    tileOverrides: adaptTileOverrides(tileItems),
    backgroundColor:
      ((section as { collectionGroupBackgroundColor?: string | null })
        .collectionGroupBackgroundColor as string) || null,
    backgroundColorEnd:
      ((section as { collectionGroupBackgroundColorEnd?: string | null })
        .collectionGroupBackgroundColorEnd as string) || null,
    backgroundGradientDirection:
      ((section as { collectionGroupBackgroundGradientDirection?: string | null })
        .collectionGroupBackgroundGradientDirection as string) || null,
    anchorSlug: ((section as { anchorSlug?: string | null }).anchorSlug as string) || null,
    collections: [],
  };
}

function adaptResponsiveMedia(media: unknown): CMSResponsiveMedia | null {
  if (!media || typeof media !== 'object') return null;
  const m = media as Record<string, unknown>;

  const desktop = adaptImageWithFocalPoint(m, m.image as Gallet.ContentfulAsset);
  if (!desktop) return null;

  return {
    desktop,
    mobile: m.imageMobile
      ? adaptImageWithFocalPoint(m, m.imageMobile as Gallet.ContentfulAsset, false)
      : null,
  };
}

/**
 * Adapt Call to Action link
 *
 * CTA data can come in different formats:
 * - Teaser format: { text, url, target, variant }
 * - Ecom format: { text, url, target, type }
 */
function adaptCallToAction(cta: unknown): CMSCallToAction | null {
  if (!cta || typeof cta !== 'object') return null;
  const c = cta as Record<string, unknown>;

  // variant can be in 'variant' or 'type' field depending on content format
  const variant = (c.variant || c.type) as UrlType;

  return {
    text: (c.text as string) || '',
    url: (c.url as string) || '',
    target: (c.target as '_blank' | '_self') || null,
    variant,
    icon: (c.icon as string) || null,
    eventType: (c.eventType as UrlEventType) || undefined,
    eventTrigger: (c.eventTrigger as CMSEvent) || null,
  };
}

/**
 * Adapt FindAStore (Gallet) for boutique panel and PlaceholderStores.
 */
function adaptFindAStore(section: GalletContentItem): CMSFindAStore {
  const cardImage = section.cardImage as Gallet.ContentfulAsset | null | undefined;
  const longImage = section.longImage as Gallet.ContentfulAsset | null | undefined;
  const richText =
    (section.findAStoreRichText as { json: unknown } | null | undefined) ??
    (section.text as { json: unknown } | null | undefined);
  const backgroundColor =
    (section.findAStoreBackgroundColor as string | null | undefined) ??
    (section.backgroundColor as string | null | undefined);

  return {
    __typename: 'FindAStore',
    sys: adaptSys(section.sys as Gallet.ContentfulSys),
    internalName: (section.internalName as string) || null,
    heading: null,
    description: null,
    cta: null,
    cardImage: cardImage ? adaptMedia(cardImage) : null,
    longImage: longImage ? adaptMedia(longImage) : null,
    backgroundColor: backgroundColor || null,
    text: richText || null,
    url: (section.url as string) || null,
    urlText: (section.urlText as string) || null,
    findAStoreData: {
      sys: adaptSys(section.sys as Gallet.ContentfulSys),
      type: (section.type as string) || null,
      url: (section.url as string) || null,
      urlText: (section.urlText as string) || null,
      backgroundColor: backgroundColor || null,
      cardImage: cardImage ? adaptMedia(cardImage) : null,
      longImage: longImage ? adaptMedia(longImage) : null,
      text: richText || null,
    } satisfies CMSFindAStoreData,
  };
}

/**
 * Card-level CTA destination (universalCard.cta1Url/cta2Url): the same CTA
 * entry (text + style) is shared across many cards, each card supplying its
 * own url; the CTA entry's url is the fallback when the card leaves it empty.
 */
function withCardCtaUrl(cta: CMSCallToAction | null, url?: string | null): CMSCallToAction | null {
  if (!cta || !url) return cta;
  return { ...cta, url };
}

/**
 * Adapt UniversalCard (BEC Universal components)
 *
 * Children (Heading, Cta, ImageWithFocalPoint, Typography) are referenced
 * sub-entries flattened into the domain type. Unlike the legacy Card, layout
 * fields live directly on the card entry.
 */
function adaptUniversalCard(section: GalletContentItem): CMSUniversalCard {
  const titleField = section.title as {
    text?: string;
    headingType?: string;
    headingSeoTag?: string;
    mainColor?: string;
    highlightColor?: string;
    headingVariation?: string;
    textAlignment?: string;
  } | null;

  const media = section.media as {
    image?: Gallet.ContentfulAsset;
    imageMobile?: Gallet.ContentfulAsset;
    video?: Gallet.ContentfulAsset;
    videoMobile?: Gallet.ContentfulAsset;
  } | null;

  const typography = section.typography as Record<string, unknown> | null;

  return {
    __typename: 'UniversalCard',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    type: (section.type as string) || null,
    subtitleTop: (section.subtitleTop as string) || null,
    subtitleBottom: (section.subtitleBottom as string) || null,
    heading: titleField?.text || null,
    headingTag: titleField?.headingSeoTag || null,
    headingType: titleField?.headingType || null,
    headingColor: titleField?.mainColor || null,
    headingHighlightColor: titleField?.highlightColor || null,
    headingVariation: titleField?.headingVariation || null,
    headingTextAlignment: titleField?.textAlignment || null,
    richText: adaptRichText(section.cardText),
    cta1: withCardCtaUrl(adaptCtaRef(section.cta1), section.cta1Url as string | null),
    cta2: withCardCtaUrl(adaptCtaRef(section.cta2), section.cta2Url as string | null),
    ctaLayout: (section.ctaLayout as string) || null,
    image: media?.image
      ? adaptImageWithFocalPoint(media as Record<string, unknown>, media.image)
      : null,
    mobileImage: media?.imageMobile
      ? adaptImageWithFocalPoint(media as Record<string, unknown>, media.imageMobile, false)
      : null,
    video: media?.video ? adaptMedia(media.video) : null,
    mobileVideo: media?.videoMobile ? adaptMedia(media.videoMobile) : null,
    videoPlayOption: ((media as Record<string, unknown> | null)?.videoPlayOption as string) || null,
    mediaHeightDesktop: (section.mediaHeightDesktop as number) ?? null,
    mediaHeightMobile: (section.mediaHeightMobile as number) ?? null,
    textColor: (typography?.textColor as string) || null,
    textSize: (typography?.textSize as string) || null,
    textAlignment: (typography?.textAlignment as string) || null,
    textMaxWidth: (typography?.textMaxWidth as string) || null,
    backgroundColor: extractColor(section.cardBackgroundColor),
    paddingTopBottom: (section.paddingTopBottom as string) || null,
    paddingLeftRight: (section.paddingLeftRight as string) || null,
    marginBottom: (section.marginBottom as string) || null,
    contentHorizontalAlignment: (section.contentHorizontalAlignment as string) || null,
    contentVerticalAlignment: (section.contentVerticalAlignment as string) || null,
    deviceDisplay: (section.deviceDisplay as string) || null,
    showMoreOn: (section.showMoreOn as string) || null,
    entryEffectType: (section.entryEffectType as string) || null,
    countdownTo: (section.countdownTo as string) || null,
  };
}

/**
 * Adapt UniversalGrid (BEC Universal components)
 * Content is restricted to UniversalCard by the content type validation.
 */
function adaptUniversalGrid(section: GalletContentItem): CMSUniversalGrid {
  const rawCollection = section.contentCollection as {
    items?: GalletContentItem[];
  } | null;
  const items = (rawCollection?.items || [])
    .filter((item): item is GalletContentItem => item != null && typeof item === 'object')
    .map((item) => adaptGalletSection(item))
    .filter((item): item is CMSUniversalCard => item?.__typename === 'UniversalCard');

  return {
    __typename: 'UniversalGrid',
    sys: adaptSys(section.sys),
    internalName: section.internalName || null,
    columnDesktop: (section.columnDesktop as number) ?? null,
    columnMobile: (section.columnMobile as number) ?? null,
    isSlider: (section.isSlider as boolean) ?? null,
    isSliderMobile: (section.isSliderMobile as boolean) ?? null,
    hasScrollBar: (section.hasScrollBar as boolean) ?? null,
    gridHeightDesktop: (section.gridHeightDesktop as number) ?? null,
    gridHeightMobile: (section.gridHeightMobile as number) ?? null,
    columnsGap: (section.columnsGap as string) || null,
    rowsGap: (section.rowsGap as string) || null,
    maxWidth: (section.maxWidth as string) || null,
    paddingTopBottom: (section.paddingTopBottom as string) || null,
    paddingLeftRight: (section.paddingLeftRight as string) || null,
    marginBottom: (section.marginBottom as string) || null,
    contentHorizontalAlignment: (section.contentHorizontalAlignment as string) || null,
    contentVerticalAlignment: (section.contentVerticalAlignment as string) || null,
    deviceDisplay: (section.deviceDisplay as string) || null,
    backgroundColor: extractColor(section.gridBackgroundColor),
    backgroundMediaDesktop: section.backgroundMediaDesktop
      ? adaptMedia(section.backgroundMediaDesktop as Gallet.ContentfulAsset)
      : null,
    backgroundMediaMobile: section.backgroundMediaMobile
      ? adaptMedia(section.backgroundMediaMobile as Gallet.ContentfulAsset)
      : null,
    entryEffectType: (section.entryEffectType as string) || null,
    contentCollection: { items },
  };
}

// ============================================
// Main Adapters
// ============================================

/**
 * Adapt a single Gallet content section to domain type
 */
export function adaptGalletSection(
  section: GalletContentItem | null | undefined
): CMSContentItem | null {
  if (!section) return null;

  const result = adaptGalletSectionInner(section);

  if (result && section.id) {
    result.anchorId = section.id as string;
  }

  if (result) {
    result.localizationConfiguration = adaptLocalizationConfiguration(
      section.localizationConfiguration
    );
  }

  return result;
}

function adaptGalletSectionInner(section: GalletContentItem): CMSContentItem | null {
  switch (section.__typename) {
    // Product page sections
    case 'PlaceholderProductDetails':
      return adaptPlaceholderProductDetails(
        section as Extract<GalletContentItem, { __typename?: 'PlaceholderProductDetails' }>
      );

    case 'Placeholder':
      return adaptPlaceholder(
        section as Extract<GalletContentItem, { __typename?: 'Placeholder' }>
      );

    case 'PlaceholderModelStory':
      return adaptPlaceholderModelStory(
        section as Extract<GalletContentItem, { __typename?: 'PlaceholderModelStory' }>
      );

    case 'TrendingProducts':
      return adaptTrendingProducts(section);

    case 'SliderProductsManual':
      return adaptSliderProductsManual(section);

    // Homepage/Page sections
    case 'HeroSlider':
      return adaptHeroSlider(section);

    case 'Card':
      return adaptCard(section);

    case 'ContentGroup':
      return adaptContentGroup(section);

    case 'Banner':
      return adaptBanner(section);

    // Gallet-specific sections
    case 'StoriesBanner':
      return adaptStoriesBanner(section);

    case 'StoriesList':
      return {
        __typename: 'StoriesList',
        sys: adaptSys(section.sys),
        internalName: section.internalName || null,
        title: section.storiesListTitle || null,
        latest: section.latest ?? null,
        filters: section.filters ?? null,
        buttonLabel: section.buttonLabel || null,
        buttonUrl: section.buttonUrl || null,
      } satisfies CMSStoriesList;

    case 'Timeline':
      return adaptTimeline(section);

    case 'CategoryBannerGrid':
      return adaptCategoryBannerGrid(section);

    case 'DoubleBanner':
      return adaptDoubleBanner(section);

    case 'EBoutiqueServices':
      return adaptEBoutiqueServices(section);

    case 'Popup':
      return adaptPopup(section);

    case 'HeroProductListing':
      return adaptHeroProductListing(section);

    case 'FindAStore':
      return adaptFindAStore(section);

    case 'ContactUs':
      return adaptContactUs(section);

    case 'FaqWrap':
      return adaptFaqWrap(section as Record<string, unknown>, (sys) =>
        adaptSys(sys as Gallet.ContentfulSys)
      );

    case 'FaqCategories':
      return adaptFaqCategories(section as Record<string, unknown>, (sys) =>
        adaptSys(sys as Gallet.ContentfulSys)
      );

    // Shared component types
    case 'Heading':
      return {
        __typename: 'Heading',
        sys: adaptSys(section.sys),
        internalName: section.internalName || null,
        text: section.text || null,
        headingType: cleanCmsConfigValue(section.headingType) || null,
        headingSeoTag: cleanCmsConfigValue(section.headingSeoTag) || null,
        headingVariation: cleanCmsConfigValue(section.headingVariation) || null,
        mainColor: extractColor(section.mainColor),
        highlightColor: extractColor(section.highlightColor),
        textAlignment: cleanCmsConfigValue(section.textAlignment) || null,
      } satisfies CMSHeading;

    case 'FreeTextContent':
      return {
        __typename: 'FreeTextContent',
        sys: adaptSys(section.sys),
        internalName: section.internalName || null,
        freeTextContent: section.textContent || null,
      } satisfies CMSFreeTextContent;

    case 'CollectionGroup':
      return adaptCollectionGroup(section);

    case 'Grid': {
      const rawCollection = section.contentCollection as {
        items?: GalletContentItem[];
      } | null;
      const adaptedItems = (rawCollection?.items || [])
        .filter((item): item is GalletContentItem => item != null && typeof item === 'object')
        .map((item) => adaptGalletSection(item))
        .filter((item): item is CMSContentItem => item !== null);
      const rawGridMaxWidth = (section.maxWidth as string) || null;
      const gridMaxWidth = rawGridMaxWidth
        ? (POPUP_MAX_WIDTH_MAP[rawGridMaxWidth.toLowerCase()] ?? null)
        : null;
      return {
        __typename: 'Grid',
        sys: adaptSys(section.sys),
        internalName: section.internalName || null,
        columns: parseGridColumnCount(section.col || section.columns),
        columnsMobile: parseGridColumnCount(section.colForMobile || section.columnsMobile),
        contentCollection: { items: adaptedItems },
        paddingTop: (section.paddingTop as string) || null,
        paddingBottom: (section.paddingBottom as string) || null,
        maxWidth: gridMaxWidth,
        backgroundColor: extractColor(section.gridBackgroundColor, section.backgroundColor) || null,
        backgroundMedia: section.backgroundMedia
          ? adaptMedia(section.backgroundMedia as Gallet.ContentfulAsset)
          : null,
        verticalAlignment:
          (section.verticalAlignment as string as CMSGrid['verticalAlignment']) || null,
        visibleInDesktop: section.visibleInDesktop ?? null,
        visibleInMobile: section.visibleInMobile ?? null,
        entryEffect: (section.entryEffect as boolean) ?? null,
      } satisfies CMSGrid;
    }

    case 'UniversalGrid':
      return adaptUniversalGrid(section);

    case 'UniversalCard':
      return adaptUniversalCard(section);

    case 'NavQuickLinks':
    case 'NavQuickLinksWithBanner':
      return {
        __typename: 'NavQuickLinks',
        sys: adaptSys(section.sys),
        internalName: section.internalName || null,
        type: section.type || null,
        isSticky: section.isSticky ?? null,
        linksCollection: section.linksCollection || null,
        navQuickLinkBackgroundColor: section.navQuickLinkBackgroundColor || null,
      } satisfies CMSNavQuickLinks;

    // ImageWithFocalPoint - pass through for MediaWrapper (preserves full structure)
    // MediaWrapper can handle this format directly via its isLegacy check
    case 'ImageWithFocalPoint':
      return {
        __typename: 'ImageWithFocalPoint',
        sys: adaptSys(section.sys),
        internalName: section.internalName || null,
        // Preserve all media fields for MediaWrapper
        image: section.image || null,
        imageMobile: section.imageMobile || null,
        video: section.video || null,
        videoMobile: section.videoMobile || null,
        focalPoint: section.focalPoint || null,
        alt: section.alt || null,
        url: section.url || null,
        imageOnHover: section.imageOnHover || null,
        imageOnHoverMobile: section.imageOnHoverMobile || null,
        videoPlayOption: section.videoPlayOption || null,
        entryEffect: section.entryEffect ?? false,
      } as CMSContentItem;

    // CMS-authored breadcrumb block (BEC-24794): the entry pilots placement
    // and style ('Light' inverts the bar); the crumbs themselves are
    // app-generated at render time. The generic fallback would drop `style`.
    case 'Breadcrumb':
      return {
        __typename: 'Breadcrumb',
        sys: adaptSys(section.sys),
        internalName: section.internalName || null,
        style: section.style || null,
      } satisfies CMSBreadcrumb;

    case 'PlaceholderService':
      return adaptPlaceholderService(
        section as Extract<GalletContentItem, { __typename?: 'PlaceholderService' }>,
        (sys) => adaptSys(sys as Gallet.ContentfulSys)
      ) satisfies CMSPlaceholderService;

    default:
      // Generic fallback for unknown types
      return adaptGenericSection(section);
  }
}

/**
 * Adapt array of Gallet sections
 */
export function adaptGalletSections(
  sections: (GalletContentItem | null | undefined)[]
): CMSContentItem[] {
  return sections
    .map(adaptGalletSection)
    .filter((s): s is CMSContentItem => s !== null)
    .map((s) => ({
      ...s,
      entryEffect: (s as CMSSection).entryEffect ?? false,
    }));
}

/**
 * Adapt Gallet page to domain page
 */
export function adaptGalletPage(page: unknown): CMSPage | null {
  if (!page || typeof page !== 'object') return null;
  const p = page as Record<string, unknown>;

  const contentItems = (p.contentCollection as { items?: unknown[] })?.items || [];
  const content = adaptGalletSections(contentItems as GalletContentItem[]);

  return {
    sys: adaptSys(p.sys as Gallet.ContentfulSys),
    slug: (p.slug as string) || '',
    pageTitle: (p.pageTitle as string) || null,
    seo: adaptSeoConfiguration(
      mergeSeoConfiguration(p.seoConfiguration, p.seoConfigurationOverride)
    ),
    content,
    localizationConfiguration: adaptLocalizationConfiguration(p.localizationConfiguration),
    mainHeader: adaptPageHeader(p.mainHeader),
  };
}

function adaptPageHeader(raw: unknown): CMSPageHeader | null {
  if (!raw || typeof raw !== 'object') return null;
  const h = raw as Record<string, unknown>;

  const logo = h.logo as { url?: string; width?: number; height?: number } | null | undefined;
  const logoDark = h.logoDark as
    | { url?: string; width?: number; height?: number }
    | null
    | undefined;
  const bgImage = h.backgroundImage as { url?: string } | null | undefined;

  return {
    background: cleanCmsConfigValue(h.background as string),
    textColorOption: extractColor(h.textColorOption),
    positionBehavior: cleanCmsConfigValue(h.positionBehavior as string),
    backgroundImage: bgImage?.url || null,
    logoLight: logo?.url
      ? {
          url: logo.url,
          title: null,
          alt: null,
          description: null,
          width: logo.width || null,
          height: logo.height || null,
          contentType: null,
          focalPoint: null,
        }
      : null,
    logoDark: logoDark?.url
      ? {
          url: logoDark.url,
          title: null,
          alt: null,
          description: null,
          width: logoDark.width || null,
          height: logoDark.height || null,
          contentType: null,
          focalPoint: null,
        }
      : null,
    logoWidthDesktop: (h.logoWidthDesktop as number) || null,
    logoHeightDesktop: (h.logoHeightDesktop as number) || null,
    logoWidthMobile: (h.logoWidthMobile as number) || null,
    logoHeightMobile: (h.logoHeightMobile as number) || null,
  };
}

/**
 * Normalize the Contentful `localizationConfiguration` JSON field to a
 * lowercase string array of country codes.
 *
 * Contentful stores this as a JSON value that can be an array of strings
 * (country codes like `['US', 'CA', 'GB']`), null, or undefined.
 */
export function adaptLocalizationConfiguration(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const codes = raw.filter((v): v is string => typeof v === 'string');
  if (codes.length === 0) return null;
  return codes.map((c) => c.toLowerCase());
}

/**
 * Decide whether an adapted item is enabled in the given country.
 * Returns `true` when no localization configuration is set (null, undefined,
 * or an empty array); otherwise checks for membership.
 *
 * The match is case-insensitive: callers pass an already-lowercased country,
 * but we still lower-case array entries defensively in case any non-adapted
 * value (e.g. from a draft-mode passthrough) reaches the filter.
 */
export function isItemEnabledInCountry(
  item: { localizationConfiguration?: string[] | null } | null | undefined,
  country: string
): boolean {
  if (!item) return false;
  const codes = item.localizationConfiguration;
  if (!codes || codes.length === 0) return true;
  return codes.some((c) => c.toLowerCase() === country);
}

/**
 * Filter an already-adapted array of content items, dropping any item (or
 * recursive child) whose `localizationConfiguration` excludes the given
 * country. Items with no configuration are kept everywhere.
 *
 * Pure: returns a new array referencing the same surviving items (or fresh
 * objects where recursion replaced inner collections). Does not mutate input.
 *
 * Recursion targets:
 *   - ContentGroup.items
 *   - Grid.contentCollection.items
 *   - HeroSlider.slides
 *   - DoubleBanner.leftBanner / rightBanner (drop to null when excluded)
 *   - FaqCategories.faqWraps and FaqWrap.categories[].questions
 *   - PlaceholderProductDetails.perks
 */
function filterFaqWrapByCountry(wrap: CMSFaq, target: string): CMSFaq | null {
  if (!isItemEnabledInCountry(wrap, target)) return null;

  const categories = (wrap.categories || [])
    .map((category) => ({
      ...category,
      questions: category.questions.filter((question) => isItemEnabledInCountry(question, target)),
    }))
    .filter((category) => category.questions.length > 0);

  if (categories.length === 0) return null;

  return { ...wrap, categories };
}

function filterPlaceholderProductDetailsByCountry(
  details: CMSPlaceholderProductDetails,
  target: string
): CMSPlaceholderProductDetails {
  if (!details.perks) return details;
  const perks = details.perks.filter((perk) => isItemEnabledInCountry(perk, target));
  return { ...details, perks };
}

function filterContentItemChildrenByCountry(
  children: CMSContentItem[],
  target: string
): CMSContentItem[] {
  return children
    .map((child) => filterContentItemByCountry(child, target))
    .filter((child): child is CMSContentItem => child !== null);
}

function filterContentItemByCountry(item: CMSContentItem, target: string): CMSContentItem | null {
  if (!isItemEnabledInCountry(item, target)) return null;

  switch (item.__typename) {
    case 'ContentGroup': {
      const group = item as CMSContentGroup;
      return { ...group, items: filterContentItemChildrenByCountry(group.items || [], target) };
    }
    case 'Grid': {
      const grid = item as CMSGrid;
      return {
        ...grid,
        contentCollection: {
          items: filterContentItemChildrenByCountry(grid.contentCollection?.items || [], target),
        },
      };
    }
    case 'UniversalGrid': {
      const grid = item as CMSUniversalGrid;
      const filtered = (grid.contentCollection?.items || []).filter((child) =>
        isItemEnabledInCountry(child, target)
      );
      return { ...grid, contentCollection: { items: filtered } };
    }
    case 'HeroSlider': {
      const slider = item as CMSHeroSlider;
      const slides = (slider.slides || []).filter((slide) => isItemEnabledInCountry(slide, target));
      return { ...slider, slides };
    }
    case 'DoubleBanner': {
      const dbl = item as CMSDoubleBanner;
      const leftKept = isItemEnabledInCountry(dbl.leftBanner, target);
      const rightKept = isItemEnabledInCountry(dbl.rightBanner, target);
      return {
        ...dbl,
        leftBanner: leftKept ? dbl.leftBanner : null,
        rightBanner: rightKept ? dbl.rightBanner : null,
      };
    }
    case 'FaqCategories': {
      const faqCategories = item as CMSFaqCategories;
      const faqWraps = (faqCategories.faqWraps || [])
        .map((wrap) => filterFaqWrapByCountry(wrap, target))
        .filter((wrap): wrap is CMSFaq => wrap !== null);
      if (faqWraps.length === 0) return null;
      return { ...faqCategories, faqWraps };
    }
    case 'FaqWrap':
      return filterFaqWrapByCountry(item as CMSFaq, target);
    case 'PlaceholderProductDetails':
      return filterPlaceholderProductDetailsByCountry(item as CMSPlaceholderProductDetails, target);
    default:
      return item;
  }
}

export function filterContentItemsByCountry(
  items: CMSContentItem[],
  country: string
): CMSContentItem[] {
  const target = country.toLowerCase();
  return filterContentItemChildrenByCountry(items || [], target);
}

/**
 * Filter an already-adapted CMSPage's content tree, dropping any section
 * (or recursive child) whose `localizationConfiguration` excludes the given
 * country. Sections with no configuration are kept everywhere.
 *
 * Thin wrapper over filterContentItemsByCountry. Page-level localization
 * (the page itself being disabled in a country) is left to the caller, since
 * it short-circuits to a 404.
 */
export function filterPageContentByCountry(page: CMSPage, country: string): CMSPage {
  return {
    ...page,
    content: filterContentItemsByCountry(page.content || [], country),
  };
}

/**
 * Adapt SEO configuration
 *
 * Contentful field names use the `seo` prefix (seoTitle, seoDescription, etc.)
 * while our domain model uses plain names (title, description, etc.).
 * The `seoIndex` boolean (true = allow indexing) is inverted to `noIndex`.
 */
function adaptSeoConfiguration(seo: unknown): CMSSeoConfiguration | null {
  if (!seo || typeof seo !== 'object') return null;
  const s = seo as Record<string, unknown>;

  const seoIndex = s.seoIndex as boolean | null | undefined;
  const noIndex = seoIndex === false ? true : seoIndex === true ? false : null;

  return {
    title: (s.seoTitle as string) || null,
    description: (s.seoDescription as string) || null,
    keywords: (s.keywords as string[]) || null,
    ogImage: s.seoThumbnail ? adaptMedia(s.seoThumbnail as Gallet.ContentfulAsset) : null,
    noIndex,
    noFollow: null,
    canonical: (s.seoCanonical as string) || null,
  };
}
